import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = { src: String, newChatSrc: String };
  static targets = ["frame", "toggle"];

  #mobile = window.matchMedia("(max-width: 767px)");
  #viewportAttached = false;
  #zendeskHidden = false;

  connect() {
    this.handleOpenOrClose();
  }

  disconnect() {
    this.#detachViewport();
    this.#setZendeskHidden(false);
  }

  handleOpenOrClose() {
    if (this.toggleTarget.checked) {
      if (!this.frameTarget.getAttribute("src")) {
        this.frameTarget.src = this.srcValue;
      }
      sessionStorage.setItem("massimo-open", "true");
      window.posthog?.register({ massimo_chat: true });
      window.posthog?.startSessionRecording();
      this.#attachViewport();
      // On mobile the sheet covers the screen, so hide the Zendesk launcher (it overlaps the composer).
      if (this.#mobile.matches) this.#setZendeskHidden(true);
    } else {
      sessionStorage.removeItem("massimo-open");
      window.posthog?.stopSessionRecording();
      this.#detachViewport();
      this.#setZendeskHidden(false);
      this.dispatch("closed");
    }
  }

  openPanel() {
    const chatKey = sessionStorage.getItem("massimo-chat-id");
    const startFresh = chatKey === "new";
    const outerLoaded = this.frameTarget.getAttribute("src");
    // Opening a specific chat (new or an existing one) should always show the chat, not
    // whatever history/chat toggle state was left over from a previous time the panel was open.
    if (chatKey && chatKey !== "history") delete this.element.dataset.activePanel;
    if (this.#mobile.matches) {
      // Full-screen sheet: collapse the mobile nav overlay behind it and drop any leftover desktop
      // fullscreen state so the sheet renders cleanly.
      const navToggle = document.getElementById("admin-nav-toggle");
      if (navToggle) navToggle.checked = true;
      sessionStorage.removeItem("massimo-fullscreen");
      delete this.element.dataset.fullscreen;
    }
    if (!this.toggleTarget.checked) {
      this.toggleTarget.checked = true;
      this.toggleTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }
    // First-ever open routes via chat_router_controller in the freshly loaded
    // outer frame; we only need loadNewChat when the outer frame is already up.
    if (startFresh && outerLoaded) this.loadNewChat();
  }

  // On mobile the sheet covers the whole screen, so tapping a link in one of Mo's responses slides the
  // sheet down (and keeps it closed) before navigating, revealing the destination page.
  maybeCloseOnLink(event) {
    if (!this.#mobile.matches) return;
    const link = event.target.closest("a[href]");
    if (!link || link.target === "_blank") return;
    // Scope to Mo's rendered message content; skips header / New Chat links and new-tab/external links.
    if (!link.closest("[data-controller~='admin--massimo--markdown']")) return;

    event.preventDefault();
    const href = link.href;
    sessionStorage.removeItem("massimo-open");
    this.toggleTarget.checked = false;
    this.toggleTarget.dispatchEvent(new Event("change", { bubbles: true }));
    // The side nav's open state is restored from the admin_nav_collapsed cookie on the next full page
    // load. Collapse it and dispatch change (so admin--sidenav persists the cookie) — otherwise we'd
    // land on the destination with the nav overlay covering the page.
    const navToggle = document.getElementById("admin-nav-toggle");
    if (navToggle) {
      navToggle.checked = true;
      navToggle.dispatchEvent(new Event("change", { bubbles: true }));
    }
    setTimeout(() => {
      window.location.href = href;
    }, 320); // let the 300ms slide-down finish before the page unloads
  }

  loadNewChat() {
    const innerFrame = document.getElementById("massimo-chat-content");
    if (!innerFrame) return;
    sessionStorage.setItem("massimo-chat-id", "new");
    // The prompt and Home's uploads stay in sessionStorage (chat_submit consumes them) — too long for the URL.
    const hasPrompt = Boolean(
      sessionStorage.getItem("massimo-prompt") ||
      sessionStorage.getItem("massimo-files"),
    );
    const autoSubmit = sessionStorage.getItem("massimo-autosubmit");
    sessionStorage.removeItem("massimo-autosubmit");
    const suggestionKey = sessionStorage.getItem("massimo-suggestion-key");
    sessionStorage.removeItem("massimo-suggestion-key");
    const source = sessionStorage.getItem("massimo-source");
    sessionStorage.removeItem("massimo-source");
    let src = this.newChatSrcValue;
    if (hasPrompt) {
      const url = new URL(src, window.location.origin);
      url.searchParams.set("has_prompt", "1");
      if (autoSubmit) url.searchParams.set("autosubmit", "1");
      if (suggestionKey) url.searchParams.set("suggestion_key", suggestionKey);
      if (source) url.searchParams.set("source", source);
      src = url.toString();
    }
    innerFrame.setAttribute("src", src);
  }

  // Keep the mobile bottom sheet sized to the visible area and above the keyboard. When the on-screen
  // keyboard opens, the visual viewport shrinks; mirroring its height into --mo-vh and the keyboard
  // height into --mo-kb (both consumed by the max-width:767px CSS) lifts the sheet so its bottom-pinned
  // composer sits above the keyboard instead of hidden behind it.
  #syncViewport = () => {
    const sidebar = this.element.querySelector(".mo-sidebar");
    const vv = window.visualViewport;
    if (!sidebar || !vv) return;
    sidebar.style.setProperty("--mo-vh", `${vv.height}px`);
    const keyboard = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    sidebar.style.setProperty("--mo-kb", `${keyboard}px`);
  };

  #attachViewport() {
    if (this.#viewportAttached || !this.#mobile.matches || !window.visualViewport) return;
    this.#viewportAttached = true;
    window.visualViewport.addEventListener("resize", this.#syncViewport);
    window.visualViewport.addEventListener("scroll", this.#syncViewport);
    this.#syncViewport();
  }

  #detachViewport() {
    if (!this.#viewportAttached) return;
    this.#viewportAttached = false;
    window.visualViewport?.removeEventListener("resize", this.#syncViewport);
    window.visualViewport?.removeEventListener("scroll", this.#syncViewport);
    const sidebar = this.element.querySelector(".mo-sidebar");
    sidebar?.style.removeProperty("--mo-vh");
    sidebar?.style.removeProperty("--mo-kb");
  }

  // Hide/show the Zendesk launcher so it doesn't cover the composer while the mobile sheet is open.
  // Only acts on a real state change and only restores the launcher if *we* hid it — so opening/closing
  // on desktop never touches Zendesk, and a launcher hidden for another reason is never force-shown.
  // Handles both the Messaging ("messenger") and Classic ("webWidget") Web Widgets; whichever namespace
  // isn't active for this account is a harmless no-op.
  #setZendeskHidden(hidden) {
    if (hidden === this.#zendeskHidden) return;
    if (typeof window.zE !== "function") return;
    const action = hidden ? "hide" : "show";
    for (const namespace of ["messenger", "webWidget"]) {
      try {
        window.zE(namespace, action);
      } catch (_) {
        /* namespace not enabled for this account */
      }
    }
    this.#zendeskHidden = hidden;
  }
}
